/**
 * Espelha `ViewModel.Swap.SwapImagemSessaoResponseViewModel` do backend.
 *
 * Retornado por `GET /api/Swap/ObterImagemSessao` (proc
 * `PR_SWAP_SESSAO_OBTEM_IMAGEM_ITEM`), que devolve uma das imagens
 * binárias da sessão (BOMBA / PANEL / PLACA) já em Base64.
 *
 * Atenção ao parâmetro `selImg`: a API usa o valor inglês `'PANEL'`,
 * embora a coluna física no banco seja `IMG_PAINEL`. O backend faz
 * a tradução; no front sempre `'PANEL'`.
 *
 * Interpretação dos campos:
 *  - `imgBase64 == null` → a sessão existe, mas a imagem está vazia
 *    (`NULL`/0 bytes) no banco.
 *  - `imgBase64` preenchido → use `montarDataUrlImagemSessao` para
 *    gerar um `data:` URL pronto para `<img [src]>`.
 *
 * O envelope da resposta (`SwapApiResponse`) também pode trazer
 * `retorno = null` com `sucesso = true`: significa que a sessão não
 * existe OU o usuário não tem permissão na frota dela (mesmo padrão
 * do `obterSessao`).
 */

/** Tipos de imagem aceitos pelo endpoint `ObterImagemSessao`. */
export type SwapImagemTipo = 'BOMBA' | 'PANEL' | 'PLACA';

export interface SwapImagemSessao {
  codSessao: number | null;
  dscSessionExterna: string;
  /** Mesmo valor enviado em `selImg` (já normalizado para upper). */
  selImg: SwapImagemTipo;
  /**
   * Mime detectado por magic numbers no backend:
   *  `image/jpeg` | `image/png` | `image/gif` | `image/webp`
   *  ou `application/octet-stream` (formato desconhecido).
   */
  mime: string;
  /** Base64 puro (sem o prefixo `data:...`). `null` se vazia no banco. */
  imgBase64: string | null;
  /** `DATALENGTH` do binário original em bytes. `0` quando vazia. */
  tamImgBin: number | null;
}

/**
 * Monta um `data:` URL para uso direto em `<img [src]>` a partir de uma
 * `SwapImagemSessao`. Retorna `null` quando a imagem não existir ou
 * estiver vazia (`imgBase64 == null`).
 *
 * O `mime` vem confiável do backend (detectado por magic numbers), por
 * isso não precisa passar por `DomSanitizer.bypassSecurityTrustUrl`.
 */
export function montarDataUrlImagemSessao(
  img: SwapImagemSessao | null | undefined,
): string | null {
  if (!img || !img.imgBase64) {
    return null;
  }
  return `data:${img.mime};base64,${img.imgBase64}`;
}
